import { generateClient } from 'aws-amplify/api';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import * as mutations from '../graphql/mutations';
import * as queries from '../graphql/queries';

const client = generateClient();

/**
 * Save calendar events for the current user
 * @param {Array} events - Array of calendar events to save
 * @returns {Promise<Object>} - Object containing saved events and additional info
 */
export const saveCalendarEvents = async (events) => {
  try {
    // Get current user with enhanced error handling
    let user;
    try {
      user = await getCurrentUser();
      if (!user) throw new Error('User not authenticated');
      
      // Get additional user attributes to ensure we have complete user data
      const attributes = await fetchUserAttributes();
      console.log('User attributes:', attributes);
      
      // Log user identity for debugging
      console.log('Current user identity:', {
        username: user.username,
        userId: user.userId,
      });
    } catch (authError) {
      console.error('Authentication error:', authError);
      throw new Error(`Authentication error: ${authError.message}. Please sign out and sign in again.`);
    }
    
    console.log(`Saving ${events.length} calendar events for user: ${user.username}`);
    
    // Process events in batches to avoid overwhelming the API
    const batchSize = 25;
    const savedEvents = [];
    const failedEvents = [];
    
    for (let i = 0; i < events.length; i += batchSize) {
      const batch = events.slice(i, i + batchSize);
      console.log(`Processing batch ${i/batchSize + 1} of ${Math.ceil(events.length/batchSize)}`);
      
      // Use Promise.all to process batch in parallel
      const batchResults = await Promise.all(
        batch.map(async (event, index) => {
          try {
            // Create a unique identifier for the event to help debugging
            const eventIndex = i + index;
            
            const input = {
              owner: user.username, // Ensure this matches the Cognito username exactly
              title: event.title || `Event ${eventIndex}`,
              description: event.description || '',
              start: event.start.toISOString(),
              end: event.end ? event.end.toISOString() : null,
              location: event.extendedProps?.location || '',
              eventType: event.extendedProps?.eventType || '',
              staff: event.extendedProps?.staff || '',
              courseCode: event.extendedProps?.courseCode || '',
              backgroundColor: event.backgroundColor || '',
              borderColor: event.borderColor || ''
            };
            
            console.log(`Saving event ${eventIndex}:`, {
              title: input.title.substring(0, 30) + (input.title.length > 30 ? '...' : ''),
              start: input.start,
              location: input.location
            });
            
            // Try with explicit authMode and enhanced error logging
            try {
              const { data, errors } = await client.graphql({
                query: mutations.createCalendarEvent,
                variables: { input },
                authMode: 'userPool' // Explicitly use Cognito User Pool auth
              });
              
              if (errors) {
                console.error('GraphQL errors for event', eventIndex, ':', errors);
                console.error('Full error details:', JSON.stringify(errors, null, 2));
                
                // Check for specific error types
                if (errors.some(err => err.errorType === "Unauthorized")) {
                  console.error('Authorization error detected. Token may be invalid or expired.');
                  throw new Error('Authorization failed. Please sign out and sign in again.');
                }
                
                failedEvents.push({ event, errors });
                return null;
              }
              
              if (!data || !data.createCalendarEvent) {
                throw new Error('No data returned from API');
              }
              
              console.log(`Successfully saved event ${eventIndex} with ID:`, data.createCalendarEvent.id);
              return data.createCalendarEvent;
            } catch (graphqlError) {
              console.error(`GraphQL API error for event ${eventIndex}:`, graphqlError);
              
              // Try one more time with different authMode as fallback
              console.log('Retrying with IAM authorization...');
              try {
                const { data } = await client.graphql({
                  query: mutations.createCalendarEvent,
                  variables: { input },
                  authMode: 'apiKey' // Try alternative auth method
                });
                
                console.log(`Successfully saved event ${eventIndex} with ID using apiKey:`, data.createCalendarEvent.id);
                return data.createCalendarEvent;
              } catch (retryError) {
                console.error(`Retry also failed for event ${eventIndex}:`, retryError);
                throw retryError;
              }
            }
          } catch (error) {
            console.error(`Error saving event ${i + index}:`, error);
            failedEvents.push({ event, error: error.message });
            return null;
          }
        })
      );
      
      savedEvents.push(...batchResults.filter(result => result !== null));
    }
    
    // Log detailed results
    console.log(`Successfully saved ${savedEvents.length} out of ${events.length} events`);
    if (failedEvents.length > 0) {
      console.error(`Failed to save ${failedEvents.length} events:`, failedEvents);
    }
    
    return {
      events: savedEvents,
      userId: user.username,
      timestamp: new Date().toISOString(),
      success: savedEvents.length > 0,
      total: events.length,
      saved: savedEvents.length,
      failed: failedEvents.length,
      failedDetails: failedEvents.length > 0 ? failedEvents : null
    };
  } catch (error) {
    console.error('Error in saveCalendarEvents:', error);
    throw error;
  }
};

/**
 * Fetch all calendar events for the current user
 * @returns {Promise<Array>} - Array of calendar events
 */
export const fetchCalendarEvents = async () => {
  try {
    // Get current user with enhanced error handling
    let user;
    try {
      user = await getCurrentUser();
      if (!user) throw new Error('User not authenticated');
    } catch (authError) {
      console.error('Authentication error in fetchCalendarEvents:', authError);
      return [];
    }
    
    console.log(`Fetching calendar events for user: ${user.username}`);
    
    const { data } = await client.graphql({
      query: queries.listCalendarEvents,
      variables: {
        filter: { owner: { eq: user.username } },
        limit: 1000 // Adjust as needed
      },
      authMode: 'userPool'
    });
    
    const events = data.listCalendarEvents.items.map(item => ({
      id: item.id,
      title: item.title,
      start: new Date(item.start),
      end: item.end ? new Date(item.end) : null,
      extendedProps: {
        location: item.location,
        staff: item.staff,
        courseCode: item.courseCode,
        eventType: item.eventType
      },
      backgroundColor: item.backgroundColor,
      borderColor: item.borderColor
    }));
    
    console.log(`Fetched ${events.length} calendar events`);
    return events;
  } catch (error) {
    console.error('Error fetching calendar events:', error);
    return [];
  }
};

/**
 * Delete all calendar events for the current user
 * @returns {Promise<boolean>} - Success status
 */
export const clearCalendarEvents = async () => {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error('User not authenticated');
    
    console.log(`Clearing calendar events for user: ${user.username}`);
    
    // First get all events for the user
    const { data } = await client.graphql({
      query: queries.listCalendarEvents,
      variables: {
        filter: { owner: { eq: user.username } },
        limit: 1000
      },
      authMode: 'userPool'
    });
    
    const events = data.listCalendarEvents.items;
    console.log(`Found ${events.length} events to delete`);
    
    // Process deletions in batches
    const batchSize = 25;
    let deletedCount = 0;
    
    for (let i = 0; i < events.length; i += batchSize) {
      const batch = events.slice(i, i + batchSize);
      console.log(`Processing deletion batch ${i/batchSize + 1} of ${Math.ceil(events.length/batchSize)}`);
      
      await Promise.all(
        batch.map(async (event) => {
          try {
            await client.graphql({
              query: mutations.deleteCalendarEvent,
              variables: { 
                input: { 
                  id: event.id,
                  _version: event._version
                } 
              },
              authMode: 'userPool'
            });
            deletedCount++;
          } catch (error) {
            console.error(`Error deleting event ID ${event.id}:`, error);
          }
        })
      );
    }
    
    console.log(`Successfully deleted ${deletedCount} out of ${events.length} events`);
    return deletedCount === events.length;
  } catch (error) {
    console.error('Error clearing calendar events:', error);
    return false;
  }
};
